import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import CategoryBlock from '~/components/CategoryBlocks/CategoryBlock';
import { cfSrcForImage } from '~/components/CategoryBlocks/utils';
import Icon from '~/components/Icon/Icon';
import { Breadcrumbs } from '~/components/isolated/Breadcrumbs';
import { CategoriesGrid1SSkeleton } from '~/components/isolated/CategoryBlocks/CategoriesGrid1S';
import { useGlobal } from '~/hooks/useGlobal';
import { useStores } from '~/hooks/useStore';

import styles from './ShopV3.module.scss';

const ShopV3 = () => {
  const { isMobile } = useGlobal();
  const { t } = useTranslation();
  const { shopCategories } = useStores();

  const LabelComponent = isMobile
    ? Typography.Text200Medium
    : Typography.Header100Medium;

  const isLoading = shopCategories.list.length === 0;

  const blocks = shopCategories.list.map((category) =>
    company.match({
      mcosmetic: (
        <Link
          key={category.id}
          to={`/c/${category.id}`}
          className={cn(
            'flex flex-col items-center justify-between gap-2',
            'bg-graphic-transparent-brand-10',
            'nth-[2n]:bg-surface-transparent-2',
            isMobile
              ? [
                  'rounded-6 px-4 pt-3',
                  'odd:row-span-3 odd:aspect-160/188 even:row-span-2 even:aspect-160/128',
                  // 1 orphan: lone odd tile → stretch full width
                  '[&:nth-last-child(1):nth-child(4n+1)]:col-span-2 [&:nth-last-child(1):nth-child(4n+1)]:row-span-2 [&:nth-last-child(1):nth-child(4n+1)]:aspect-328/128',
                  // 2 orphans: odd+even, equalize heights
                  '[&:nth-last-child(2):nth-child(4n+1)]:row-span-2 [&:nth-last-child(2):nth-child(4n+1)]:aspect-160/128',
                  // 3 orphans: first odd gets equalized height to match the even beside it
                  '[&:nth-last-child(3):nth-child(4n+1)]:row-span-2 [&:nth-last-child(3):nth-child(4n+1)]:aspect-160/128',
                  // 3 orphans: last odd tile alone in its row → stretch full width
                  '[&:nth-last-child(1):nth-child(4n+3)]:col-span-2 [&:nth-last-child(1):nth-child(4n+3)]:row-span-2 [&:nth-last-child(1):nth-child(4n+3)]:aspect-328/128',
                  'nth-[6n+7]:bg-graphic-transparent-brand-2-15!',
                ]
              : 'aspect-338/250 rounded-6.5 nth-[6n+7]:bg-graphic-transparent-brand-2-10! px-8 pt-6',
          )}
        >
          <LabelComponent as="span" className="text-center">
            {category.name}
          </LabelComponent>
          {category.previewImage && (
            <div
              style={{
                backgroundImage: `url(${cfSrcForImage(category.previewImage)})`,
              }}
              className="w-full flex-1 bg-bottom bg-contain bg-no-repeat"
            />
          )}
        </Link>
      ),
      default: <CategoryBlock key={category.id} category={category} />,
    }),
  );

  return (
    <div className={styles.root} data-company={company.name}>
      <Helmet>
        <meta data-react-helmet="true" name="robots" content="index, follow" />
        <title>{t('meta:shopTitle')}</title>
        <meta name="description" content={t('meta:shopDescription')} />
      </Helmet>
      <div className={classes([styles.contentLayout, 'content-layout'])}>
        <div className={styles.content}>
          {isMobile ? (
            <div className={styles.search}>
              <Link to="/search" className={styles.searchBtn}>
                <Icon type="search-alt" size={16} />
                <span>{t('header:searchProducts')}</span>
              </Link>
            </div>
          ) : (
            <Breadcrumbs
              className={classes([styles.breadcrumbs, styles.newDesign])}
              items={[
                { link: '/', label: t('home') },
                { label: t('allCategories') },
              ]}
            />
          )}
          {isLoading ? (
            <div className={styles.blocksWrapper}>
              <CategoriesGrid1SSkeleton />
              <CategoriesGrid1SSkeleton />
              <CategoriesGrid1SSkeleton />
            </div>
          ) : (
            <div
              className={company.match({
                mcosmetic:
                  'grid grid-cols-2 gap-2 pt-safe-or-14 md:grid-cols-4 md:gap-4 md:pb-40',
                default: styles.blocksWrapper,
              })}
            >
              {blocks}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(ShopV3);
